import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ClientConfig, ClientConfigIO, DefaultAction } from '@core/domains/config/client-config';
import { map, skipWhile, tap } from 'rxjs/operators';
import { DomainList, DomainListIO } from '@core/domains/destination/domain-list/domain-list';
import { EnvironmentService } from '@core/domains/environment/environment.service';

export interface ConfigData {
  default_action: string;
  policy_order: string[];
  tls_is_active: boolean;
}

export interface AntivirusConfigIO {
  is_active: boolean;
  excluded_domains_lists_ids: string[] | undefined;
  excluded_domains_lists_objects: DomainListIO[] | undefined;
}

export interface Audit {
  criminal_risk: boolean;
  security_risk: boolean;
  final_rule: boolean;
  decryption: boolean;
  antivirus: 'ACTIVE' | 'INEFFECTIVE' | 'INACTIVE';
}

export type AuditStatus = 'ok' | 'warning' | 'danger';

export class AntivirusConfig {
  is_active: boolean;
  excluded_domains_lists_ids: string[] = [];
  excluded_domains_lists: DomainList[] = [];

  constructor(json: AntivirusConfigIO) {
    this.is_active = json.is_active;
    this.excluded_domains_lists_ids = json.excluded_domains_lists_ids ?? [];
    this.excluded_domains_lists = json.excluded_domains_lists_objects ? json.excluded_domains_lists_objects.map((item: DomainListIO) => new DomainList(item))
                                                                      : [];
  }

  get length(): number {
    return this.excluded_domains_lists_ids.length;
  }
}

export class ConfigSecurityAudit {
  criminal_risk: boolean;
  security_risk: boolean;
  default_rule: boolean;
  decryption: boolean;
  antivirus: 'ACTIVE' | 'INEFFECTIVE' | 'INACTIVE';

  constructor(json: Audit) {
    this.antivirus = json.antivirus;
    this.criminal_risk = json.criminal_risk;
    this.security_risk = json.security_risk;
    this.decryption = json.decryption;
    this.default_rule = json.final_rule;
  }

  get av_status(): AuditStatus {
    return this.antivirus === 'ACTIVE' ? 'ok' : this.antivirus === 'INEFFECTIVE' ? 'warning' : 'danger';
  }
  get criminal_status(): AuditStatus {
    return this.criminal_risk ? 'ok' : 'danger';
  }
  get security_status(): AuditStatus {
    return this.security_risk ? 'ok' : 'danger';
  }
  get decryption_status(): AuditStatus {
    return this.decryption ? 'ok' : 'danger';
  }
  get default_rule_status(): AuditStatus {
    return this.default_rule ? 'ok' : 'danger';
  }

  get shield_status(): number {
    return [
      this.av_status,
      this.criminal_status,
      this.security_status,
      this.decryption_status,
      this.default_rule_status
    ].filter((status: AuditStatus) => status === 'ok').length;
  }
}

@Injectable()
export class ClientConfigService {
  private _baseApiUrl: string;
  private _url: string;

  private _config = new BehaviorSubject<ClientConfig | null>(null);
  public config = this._config.asObservable().pipe(skipWhile((v) => v === null)) as Observable<ClientConfig>;

  private _antivirusConfig = new BehaviorSubject<AntivirusConfig | null>(null);
  public antivirusConfig = this._antivirusConfig.asObservable()

  constructor(
    protected envService: EnvironmentService,
    private httpClient: HttpClient
  ) {
    this._baseApiUrl = `${this.envService.environment.api_domain}/api`;
    this._url = `${this._baseApiUrl}/url_filter_v2/config/`;
  }

  // CLIENT CONFIG
  read(): Observable<ClientConfig> {
    return this.httpClient.get<ClientConfigIO>(this._url).pipe(
      tap((val) => this._config.next(new ClientConfig(val))),
      map((val: ClientConfigIO) => new ClientConfig(val))
    );
  }

  patch(payload: Partial<ClientConfig>): Observable<ClientConfigIO> {
    return this.httpClient.patch<ClientConfigIO>(this._url, payload).pipe(
      tap((val: ClientConfigIO) => this._config.next(new ClientConfig(val)))
    );
  }

  update_default_action(default_action: DefaultAction): Observable<ClientConfigIO> {
    return this.httpClient.patch<ClientConfigIO>(this._url, {default_action}).pipe(tap((val: ClientConfigIO) => {
      this._config.next(new ClientConfig(val));
    }));
  }

  update_policy_order(policy_order: string[]): Observable<ClientConfigIO> {
    return this.httpClient.patch<ClientConfigIO>(this._url, {policy_order}).pipe(tap((val: ClientConfigIO) => {
      this._config.next(new ClientConfig(val));
    }));
  }

  update_tls_is_active(tls_is_active: boolean): Observable<ClientConfigIO> {
    return this.httpClient.patch<ClientConfigIO>(this._url, {tls_is_active}).pipe(tap((val: ClientConfigIO) => {
      this._config.next(new ClientConfig(val));
    }));
  }

  // ANTIVIRUS CONFIG
  fetchAntivirusConfig(): Observable<AntivirusConfig> {
    return this.httpClient.get<AntivirusConfigIO>(`${this._baseApiUrl}/antivirus/config/`).pipe(
      map((result: AntivirusConfigIO) => new AntivirusConfig(result)),
      tap((result: AntivirusConfig) => this._antivirusConfig.next(result)),
    )
  }

  patchAntivirusConfig(payload: Partial<AntivirusConfig>): Observable<AntivirusConfig> {
    return this.httpClient.patch<AntivirusConfigIO>(`${this._baseApiUrl}/antivirus/config/`, payload).pipe(
      map((response: AntivirusConfigIO) => new AntivirusConfig(response)),
      tap((result: AntivirusConfig) => this._antivirusConfig.next(result))
    )
  }

  // CONFIG SECURITY AUDIT
  fetchAudit(): Observable<ConfigSecurityAudit> {
    return this.httpClient.get<Audit>(`${this._baseApiUrl}/config_security/config_security_audit`).pipe(
      map((response: Audit) => new ConfigSecurityAudit(response))
    )
  }
}
